import React, { useState, useEffect, useCallback, useRef } from "react";
import DownloadModalTables from "../../components/DownloadModalTables";
import DateFilter from "../../components/DateFilter";
import PortalPopup from "../../components/PortalPopup";
import DebtorDeletion from "../../components/DebtorDeletion";
import ImagePreview from "../../components/ImagePreview";
import LBAReminder from "../../components/LBAReminder";
import NavigationDropdown from "../../components/NavigationDropdown";
import LogoutConfirmation from "../../components/LogoutConfirmation";
import { useNavigate } from "react-router-dom";
import NavigationPanel from "../../components/NavigationPanel";
import Footer from "../../components/Footer.js";
import { getCurrentUser } from "../../utils/getCurrentUser";
import { debtorDetailsService } from "../../services/debtorDetailsService";
import { openBankingService } from "../../services/openBankingService";
import { Eye, Location, Sms } from "iconsax-reactjs";
import LawFirm from "../../components/LawFirm";
import { useLocation } from "react-router-dom";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { X as CloseIcon, Download as DownloadIcon, FileText as FileTextIcon, Eye as EyeIcon } from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL;

/* ─── Currency helper ───
   Accepts an ISO code ("GBP", "USD", "ZAR", "EUR"…) and returns its symbol.
   If the value is already a symbol (e.g. "£") Intl throws and it's returned as-is.
   Falls back to "£" when no currency is set (legacy records). */
const getCurrencySymbol = (currency) => {
  if (!currency) return "£";
  try {
    const parts = new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: String(currency).toUpperCase(),
      currencyDisplay: "narrowSymbol",
    }).formatToParts(0);
    return parts.find((p) => p.type === "currency")?.value || String(currency);
  } catch {
    return String(currency);
  }
};

/* ─── Styles ─── */
const dashStyles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
  html body { background: rgb(241,245,249) !important; }
  .lba2-root * { box-sizing: border-box; }
  .lba2-root { font-family: 'DM Sans', sans-serif; background: rgb(241,245,249) !important; min-height: 100vh; }
  .lba2-card { background: #fff; border: 1px solid #E2E8F0; border-radius: 16px; box-shadow: 0 1px 3px rgba(0,0,0,.06), 0 2px 12px rgba(0,0,0,.04); transition: box-shadow .2s, transform .2s; position: relative; overflow: hidden; }
  .lba2-card:hover { box-shadow: 0 4px 24px rgba(0,0,0,.09); }
  .lba2-metric-card { cursor: default; }
  .lba2-metric-card:hover { transform: translateY(-3px); }
  .lba2-metric-card .accent-bar { position: absolute; top: 0; left: 0; right: 0; height: 4px; border-radius: 16px 16px 0 0; }
  .lba2-section-title { font-size: 16px; font-weight: 700; color: #0F172A; }
  .lba2-section-sub { font-size: 13px; color: #64748B; margin-top: 2px; }
  .lba2-table { width: 100%; border-collapse: collapse; }
  .lba2-table thead tr { background: #F8FAFC; }
  .lba2-table th { padding: 11px 16px; text-align: left; font-size: 11px; font-weight: 700; color: #94A3B8; text-transform: uppercase; letter-spacing: .07em; border-bottom: 1px solid #E2E8F0; }
  .lba2-table td { padding: 14px 16px; font-size: 14px; color: #1E293B; border-bottom: 1px solid #F1F5F9; vertical-align: middle; }
  .lba2-table tbody tr { transition: background .12s; }
  .lba2-table tbody tr:hover { background: #F8FAFC; }
  .lba2-table tbody tr:last-child td { border-bottom: none; }
  .lba2-search { display: flex; align-items: center; gap: 8px; background: #fff; border: 1px solid #E2E8F0; border-radius: 10px; padding: 9px 14px; width: 294px; transition: border-color .15s, box-shadow .15s; }
  .lba2-search:focus-within { border-color: #2563EB; box-shadow: 0 0 0 3px rgba(37,99,235,.1); }
  .lba2-search input { border: none; outline: none; background: transparent; font-family: 'DM Sans', sans-serif; font-size: 14px; color: #0F172A; width: 100%; }
  .lba2-search input::placeholder { color: #94A3B8; }
  .lba2-filter-btn { display: inline-flex; align-items: center; gap: 6px; padding: 9px 14px; background: #fff; border: 1px solid #E2E8F0; border-radius: 10px; font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600; color: #0F172A; cursor: pointer; transition: border-color .15s; }
  .lba2-filter-btn:hover { border-color: #2563EB; }
  .lba2-dl-btn { display: inline-flex; align-items: center; gap: 6px; padding: 8px 16px; background: #fff; border: 1px solid #E2E8F0; border-radius: 8px; font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600; color: #1E293B; cursor: pointer; transition: border-color .15s, background .15s, color .15s; }
  .lba2-dl-btn:hover { border-color: #2563EB; background: #EFF6FF; color: #2563EB; }
  .lba2-count-pill { display: inline-flex; align-items: center; gap: 5px; padding: 3px 10px; background: #EFF6FF; border-radius: 20px; font-size: 12px; font-weight: 700; color: #2563EB; }
  .lba2-badge { display: inline-flex; align-items: center; gap: 5px; padding: 3px 10px; border-radius: 20px; font-size: 12px; font-weight: 600; }
  .lba2-action-btn { display: inline-flex; align-items: center; gap: 6px; padding: 8px 18px; background: #fff; border: 1px solid #E2E8F0; border-radius: 8px; font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600; color: #0F172A; cursor: pointer; transition: border-color .15s, background .15s, color .15s; }
  .lba2-action-btn:hover { border-color: #2563EB; background: #EFF6FF; color: #2563EB; }
  .lba2-action-btn:disabled { opacity: .5; cursor: not-allowed; }
  .lba2-mono { font-family: 'DM Mono', monospace; }
  .lba2-doc-card { width: 240px; background: #fff; border: 1px solid #E2E8F0; border-radius: 16px; box-shadow: 0 1px 3px rgba(0,0,0,.06); overflow: hidden; transition: box-shadow .2s, transform .2s; flex-shrink: 0; }
  .lba2-doc-card:hover { box-shadow: 0 6px 24px rgba(0,0,0,.1); transform: translateY(-2px); }
  .lba2-doc-preview { height: 140px; background: #F8FAFC; display: flex; align-items: center; justify-content: center; border-bottom: 1px solid #E2E8F0; }
  .lba2-doc-footer { padding: 12px 14px; display: flex; align-items: center; gap: 10px; }
  .lba2-eye-btn { background: #EFF6FF; border: 1px solid #BFDBFE; border-radius: 8px; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: background .15s; flex-shrink: 0; }
  .lba2-eye-btn:hover { background: #DBEAFE; }
  .lba2-breadcrumb { display: flex; align-items: center; gap: 6px; font-size: 13px; color: #64748B; }
  .lba2-breadcrumb a { color: #64748B; text-decoration: none; }
  .lba2-breadcrumb a:hover { color: #2563EB; }
  .lba2-breadcrumb .active { color: #0F172A; font-weight: 600; }
  .lba2-empty-docs { width: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 48px 24px; gap: 10px; color: #94A3B8; text-align: center; }
  .lba2-spinner { display: inline-block; width: 18px; height: 18px; border: 2px solid #E2E8F0; border-top-color: #2563EB; border-radius: 50%; animation: lba2-spin .7s linear infinite; }
  @keyframes lba2-spin { to { transform: rotate(360deg); } }
`;

/* ─── FilePreviewModal ─── */
const FilePreviewModal = ({ fileUrl, fileType, onClose }) => (
  <div style={{ position:"fixed", inset:0, zIndex:50, display:"flex", alignItems:"center", justifyContent:"center", background:"rgba(0,0,0,0.5)" }}>
    <div style={{ background:"#fff", padding:24, borderRadius:16, maxWidth:900, width:"100%", maxHeight:"90vh", overflow:"auto", position:"relative", boxShadow:"0 20px 60px rgba(0,0,0,0.3)" }}>
      <button onClick={onClose} style={{ position:"absolute", top:16, right:16, background:"#F1F5F9", border:"none", borderRadius:8, width:32, height:32, cursor:"pointer", fontSize:18, fontWeight:700, color:"#64748B", display:"flex", alignItems:"center", justifyContent:"center" }}>×</button>
      {fileType === "pdf" ? (
        <iframe src={fileUrl} width="100%" height="600px" title="PDF Preview" style={{ border:"none", borderRadius:8 }} />
      ) : fileType?.match(/jpg|jpeg|png|gif/i) ? (
        <img src={fileUrl} alt="Preview" style={{ maxWidth:"100%", maxHeight:600, objectFit:"contain", borderRadius:8 }} />
      ) : (
        <iframe src={fileUrl} width="100%" height="600px" title="Preview" style={{ border:"none", borderRadius:8 }} />
      )}
    </div>
  </div>
);

/* ─── FlatFileCard ─── */
const FlatFileCard = ({ file, onPreview }) => {
  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return "—";
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  };

  const name = file.fileName || file.name || "Document";
  const ext  = name.split(".").pop().toLowerCase();
  const icon = ext === "pdf"
    ? "assets/pdf.svg"
    : ext === "docx" || ext === "doc"
      ? "assets/word_file.svg"
      : "assets/file.svg";

  const displayName = name.length > 24 ? `${name.substring(0, 24)}…` : name;
  const size        = file.fileSize || file.size || 0;

  return (
    <div className="lba2-doc-card">
      <div className="lba2-doc-preview">
        <img src={icon} alt={ext} style={{ width:56, height:56, opacity:0.6 }} />
      </div>
      <div className="lba2-doc-footer">
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontWeight:700, fontSize:13, color:"#0F172A", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }} title={name}>
            {displayName}
          </div>
          <div style={{ fontSize:11, color:"#94A3B8", marginTop:2 }}>
            {ext.toUpperCase()} · {formatFileSize(size)}
          </div>
        </div>
        <button className="lba2-eye-btn" onClick={onPreview} title="Preview">
          <Eye size={16} color="#2563EB" />
        </button>
      </div>
    </div>
  );
};

/* ─── InvalidStateScreen ─── */
const InvalidStateScreen = () => (
  <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:"rgb(241,245,249)", fontFamily:"'DM Sans',sans-serif" }}>
    <div style={{ background:"#fff", borderRadius:16, padding:"48px 40px", border:"1px solid #E2E8F0", boxShadow:"0 4px 24px rgba(0,0,0,.08)", textAlign:"center", maxWidth:400 }}>
      <div style={{ fontSize:40, marginBottom:16 }}>⚠️</div>
      <div style={{ fontSize:18, fontWeight:700, color:"#0F172A", marginBottom:8 }}>Missing debtor data</div>
      <div style={{ fontSize:14, color:"#64748B", marginBottom:24 }}>This page was opened without the required debtor information. Please navigate here from the LBA list.</div>
      <a href="/lba" style={{ display:"inline-block", padding:"10px 24px", background:"#2563EB", color:"#fff", borderRadius:8, fontWeight:600, fontSize:14, textDecoration:"none" }}>Back to LBA list</a>
    </div>
  </div>
);

/* ─── Main Component ─── */
const LBA_2 = () => {
  const location = useLocation();
  const lbaId    = location.state?.lbaId;
  const userId   = location.state?.userId;

  if (!lbaId || !userId) {
    console.error("LBA_2: missing lbaId or userId in location.state", location.state);
    return <InvalidStateScreen />;
  }

  return <LBA2Inner lbaId={lbaId} userId={userId} locationState={location.state} />;
};

/* ─── Inner Component ─── */
const LBA2Inner = ({ lbaId, userId, locationState }) => {
  const navigate = useNavigate();
  const token    = localStorage.getItem("authToken");

  // ── Currency (from the debtor record; defaults to £ for legacy records) ──
  const currencyCode   = locationState?.currency;
  const currencySymbol = getCurrencySymbol(currencyCode);

  const [isDownloadModalTablesOpen, setDownloadModalTablesOpen] = useState(false);
  const [isDateFilterOpen,          setDateFilterOpen]          = useState(false);
  const [isDebtorDeletionOpen,      setDebtorDeletionOpen]      = useState(false);
  const [isImagePreviewOpen,        setImagePreviewOpen]        = useState(false);
  const [isLBAReminderOpen,         setLBAReminderOpen]         = useState(false);
  const [isNavigationDropdownOpen,  setNavigationDropdownOpen]  = useState(false);
  const [isLogoutConfirmationOpen,  setLogoutConfirmationOpen]  = useState(false);
  const [isLawFirmOpen,             setLawFirmOpen]             = useState(false);
  const [filePreview,               setFilePreview]             = useState(null);
  const [preLbaPreviewUrl,          setPreLbaPreviewUrl]        = useState(null);
  const [isBuildingPreLbaPreview,   setIsBuildingPreLbaPreview] = useState(false);
  const preLbaObjectUrlsRef = useRef([]);

  const [dueDate,      setDueDate]      = useState(null);
  const [overdueDays,  setOverdueDays]  = useState(0);
  const [invoiceCount, setInvoiceCount] = useState(0);
  const [loading,      setLoading]      = useState(true);
  const [documents,    setDocuments]    = useState([]);

  const [isSettling,           setIsSettling]           = useState(false);
  const [communicationHistory, setCommunicationHistory] = useState([]);
  const [loadingHistory,       setLoadingHistory]       = useState(true);
  const [searchTerm,           setSearchTerm]           = useState("");
  const [selectedDateTimeRange,setSelectedDateTimeRange]= useState("");
  const [statuses,             setStatuses]             = useState({});

  // ── Open Banking payout status ──────────────────────────────────────
  // Same call/shape as LbaForm.js — used so the reconstructed "sent"
  // letter can render the same "Pay Your Outstanding Balance by EFT"
  // panel the original letter would have shown at send time.
  const [openBankingStatus, setOpenBankingStatus] = useState(null);

  useEffect(() => {
    openBankingService
      .getStatus()
      .then(setOpenBankingStatus)
      .catch((err) => console.error("Failed to load Open Banking status:", err));
  }, []);

  const openLawFirm  = useCallback(() => setLawFirmOpen(true),  []);
  const closeLawFirm = useCallback(() => setLawFirmOpen(false), []);

  // ── Date the Pre-LBA was actually sent ──────────────────────────────
  // The +15-day deadline printed on the letter needs to be anchored to
  // when it was sent, not to today (today drifts further from the real
  // deadline every day this preview is reopened). There's no dedicated
  // "sentAt" field on this page, so the earliest communication-history
  // entry — logged at send time — is used as the best available proxy.
  // Falls back to null (buildPreLbaPdf then falls back to today) if no
  // history has loaded yet.
  const preLbaSentDate = communicationHistory.length
    ? new Date(Math.min(...communicationHistory.map((e) => new Date(e.createdAt).getTime())))
    : null;

  // ── Payment deadline (sent date + 15 days) ──────────────────────────
  // Same deadline shown in the reconstructed Pre-LBA letter's urgency
  // callout — surfaced here on the dashboard so it's visible at a glance
  // without opening the letter.
  const paymentDeadlineDate = preLbaSentDate ? new Date(preLbaSentDate) : null;
  if (paymentDeadlineDate) paymentDeadlineDate.setDate(paymentDeadlineDate.getDate() + 15);
  const paymentDeadlineLabel = paymentDeadlineDate
    ? paymentDeadlineDate.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
    : "—";

  useEffect(() => {
    return () => {
      preLbaObjectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  const calculateOverdueDays = (d) => {
    if (!d) return 0;
    const today = new Date(); const due = new Date(d);
    today.setHours(0,0,0,0); due.setHours(0,0,0,0);
    const diff = Math.floor((today - due) / 86400000);
    return diff > 0 ? diff : 0;
  };

  // ── Reconstruct the sent Pre-LBA letter ─────────────────────────────
  // Mirrors LBAConfirmation.jsx's generatePDF exactly (colors, layout,
  // section order, copy) — that's the actual template used to build the
  // PDF that gets emailed at send-time. Rebuilt here from this debtor's
  // current record (name/address/amount/dueDate/lbaId) since the original
  // PDF isn't persisted anywhere; fields not available on this page
  // (invoice number, company name) fall back to N/A, same as the original
  // does for missing data. Also includes the "Pay Your Outstanding Balance
  // by EFT" panel from LbaForm.js's live preview, shown whenever an Open
  // Banking payout account is connected (verified or unverified).
  const buildPreLbaPdf = useCallback(() => {
    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const primaryColor        = [13, 99, 119];
    const neutralColor        = [30, 30, 30];
    const mutedColor          = [100, 116, 139];
    const linkColor           = [37, 99, 235];
    const neutralBorderColor  = [226, 226, 226];
    const whiteColor          = [255, 255, 255];
    const offWhiteColor       = [248, 248, 248];
    const advantageHeaderColor   = [233, 240, 251];
    const consequenceHeaderColor = [255, 246, 234];
    const consequenceTextColor   = [196, 92, 0];
    const boxBorderColor  = [211, 211, 211];

    // jsPDF's built-in helvetica only covers Latin-1 (£ $ € ¥ R etc.).
    // For symbols outside that range (₹ ₦ ₱ …) fall back to the ISO code
    // so the amount never renders as garbled glyphs in the PDF.
    const pdfCurrency = /^[\x20-\xFF]+$/.test(currencySymbol)
      ? currencySymbol
      : `${String(currencyCode || "").toUpperCase()} `;

    const today = new Date();
    const formattedToday = today.toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
    // Deadline is +15 days from when the letter was actually sent, not
    // from today — falls back to today only if no sent date is known yet.
    const futureDate = preLbaSentDate ? new Date(preLbaSentDate) : new Date();
    futureDate.setDate(futureDate.getDate() + 15);
    const formattedFutureDate = futureDate.toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });

    const pageWidth = 210, pageHeight = 297, margin = 20;
    const contentWidth = pageWidth - 2 * margin;
    const effectivePageHeight = pageHeight - margin;
    let yPos = margin;

    const addRect = (x, y, w, h, fill, stroke) => {
      if (fill) pdf.setFillColor(...fill);
      if (stroke) pdf.setDrawColor(...stroke);
      pdf.rect(x, y, w, h, fill && stroke ? "FD" : fill ? "F" : "D");
    };

    const addText = (text, x, y, opts = {}) => {
      const { fontSize = 10, isBold = false, color = neutralColor, align = "left", maxWidth = contentWidth } = opts;
      pdf.setFontSize(fontSize); pdf.setFont("helvetica", isBold ? "bold" : "normal");
      pdf.setTextColor(...color);
      const lines = pdf.splitTextToSize(text, maxWidth);
      pdf.text(lines, x, y, { align });
      return lines.length * (fontSize * 0.35) + 2;
    };

    const checkAndAddPage = (spaceNeeded) => {
      if (yPos + spaceNeeded > effectivePageHeight) { pdf.addPage(); yPos = margin; return true; }
      return false;
    };

    // Plain "6ELA" wordmark, drawn as real text rather than the "∆" glyph —
    // helvetica renders that character unreliably (it was showing up
    // garbled/spaced-out in the preview).
    const drawBrandMark = (x, y, opts = {}) => {
      const { fontSize = 16, color = neutralColor, isBold = true, align = "left" } = opts;
      pdf.setFontSize(fontSize);
      pdf.setFont("helvetica", isBold ? "bold" : "normal");
      pdf.setTextColor(...color);
      const label = "6ELA";
      const totalWidth = pdf.getTextWidth(label);
      let startX = x;
      if (align === "center") startX = x - totalWidth / 2;
      else if (align === "right") startX = x - totalWidth;
      pdf.text(label, startX, y);
      return totalWidth;
    };

    // Left-aligned word-wrapped text where individual words can be bold —
    // used for the urgency callout so the deadline date stands out.
    const addWrappedLeft = (segments, x, startY, maxWidth, opts = {}) => {
      const { fontSize = 9, lineHeight = 4, color = neutralColor } = opts;
      pdf.setFontSize(fontSize);
      const words = [];
      segments.forEach((seg) => {
        seg.text.split(" ").forEach((w) => { if (w) words.push({ text: w, bold: seg.bold }); });
      });
      pdf.setFont("helvetica", "normal");
      const spaceWidth = pdf.getTextWidth(" ");
      const lines = [];
      let currentLine = [], currentWidth = 0;
      words.forEach((word) => {
        pdf.setFont("helvetica", word.bold ? "bold" : "normal");
        const wWidth = pdf.getTextWidth(word.text);
        const addWidth = currentLine.length ? spaceWidth + wWidth : wWidth;
        if (currentWidth + addWidth > maxWidth && currentLine.length) {
          lines.push(currentLine); currentLine = [word]; currentWidth = wWidth;
        } else {
          currentLine.push(word); currentWidth += addWidth;
        }
      });
      if (currentLine.length) lines.push(currentLine);
      lines.forEach((line, i) => {
        let cursorX = x;
        const y = startY + i * lineHeight;
        line.forEach((word) => {
          pdf.setFont("helvetica", word.bold ? "bold" : "normal");
          pdf.setTextColor(...color);
          pdf.text(word.text, cursorX, y);
          cursorX += pdf.getTextWidth(word.text) + spaceWidth;
        });
      });
      return lines.length;
    };

    // Caps header + tick-mark checklist column; returns the Y position
    // after the last item so both columns can be measured before the
    // shared divider height is fixed.
    const addChecklistColumn = (label, items, x, colWidth, labelColor, itemColor, subLabel) => {
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(9.5);
      pdf.setTextColor(...labelColor);
      const headerLines = pdf.splitTextToSize(label, colWidth - 4);
      pdf.text(headerLines, x, yPos + 8, { charSpace: 0.2 });
      let itemY = yPos + 8 + headerLines.length * 4.6 + 4;
      if (subLabel) {
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(7.5);
        pdf.setTextColor(...mutedColor);
        const subLines = pdf.splitTextToSize(subLabel, colWidth - 4);
        pdf.text(subLines, x, itemY);
        itemY += subLines.length * 3.6 + 2.5;
      }
      items.forEach((item) => {
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(8.5);
        pdf.setTextColor(...itemColor);
        pdf.text("\u2713", x, itemY);
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(...neutralColor);
        const lines = pdf.splitTextToSize(item, colWidth - 9);
        pdf.text(lines, x + 5, itemY);
        itemY += lines.length * 4.2 + 2.8;
      });
      return itemY;
    };

    yPos = margin;

    // ── Logo ──
    const logoWidth = 34, logoHeight = 15.2;
    pdf.setFillColor(255, 255, 255);
    pdf.rect(margin, yPos, logoWidth, logoHeight, "F");
    try {
      let loaded = false;
      for (const path of ["assets/logo-horizontal-lba-form.png", "/assets/logo-horizontal-lba-form.png", `${window.location.origin}/assets/logo-horizontal-lba-form.png`]) {
        try { pdf.addImage(path, "PNG", margin, yPos, logoWidth, logoHeight, undefined, "FAST"); loaded = true; break; } catch {}
      }
      if (!loaded) throw new Error("Logo failed");
    } catch {
      pdf.setFillColor(...primaryColor);
      pdf.roundedRect(margin, yPos, logoWidth, logoHeight, 2, 2, "F");
      drawBrandMark(margin + logoWidth / 2, yPos + logoHeight / 2 + 3, { fontSize: 16, color: [255, 255, 255], align: "center" });
    }

    // ── Reference (top right) ──
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.setTextColor(...mutedColor);
    pdf.text("REFERENCE", margin + contentWidth, yPos + 3, { align: "right", charSpace: 0.3 });
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(15);
    pdf.setTextColor(...neutralColor);
    pdf.text(String(lbaId || "D9001"), margin + contentWidth, yPos + 10, { align: "right" });
    yPos += logoHeight + 5;

    pdf.setDrawColor(...neutralBorderColor);
    pdf.setLineWidth(0.3);
    pdf.line(margin, yPos, margin + contentWidth, yPos);
    yPos += 7;

    // ── Meta row: DATE / RECIPIENT / BUSINESS ──
    const metaCols = [
      { label: "DATE", value: formattedToday },
      { label: "RECIPIENT", value: locationState?.name || "N/A" },
      { label: "BUSINESS", value: locationState?.businessName || locationState?.name || "N/A" },
    ];
    const metaColWidth = contentWidth / metaCols.length;
    metaCols.forEach((col, i) => {
      const x = margin + i * metaColWidth;
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(7.5);
      pdf.setTextColor(...mutedColor);
      pdf.text(col.label, x, yPos, { charSpace: 0.3 });
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(10.5);
      pdf.setTextColor(...neutralColor);
      pdf.text(col.value, x, yPos + 6);
    });
    yPos += 13;

    // ── Address ──
    const addressParts = [
      locationState?.address?.house,
      locationState?.address?.street,
      locationState?.address?.city,
      locationState?.address?.country,
      locationState?.address?.postalCode,
    ].filter(Boolean);
    if (addressParts.length > 0) {
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(7.5);
      pdf.setTextColor(...mutedColor);
      pdf.text("ADDRESS", margin, yPos, { charSpace: 0.3 });
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(9.5);
      pdf.setTextColor(...neutralColor);
      const addrLines = pdf.splitTextToSize(addressParts.join(", "), contentWidth);
      pdf.text(addrLines, margin, yPos + 5.5);
      yPos += 9 + addrLines.length * 4.2;
    }

    // ── FINAL PRE-LEGAL NOTICE ──
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(15);
    pdf.setTextColor(...consequenceTextColor);
    pdf.text("FINAL PRE-LEGAL NOTICE", margin, yPos, { charSpace: 0.2 });
    yPos += 6;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9.5);
    pdf.setTextColor(...neutralColor);
    pdf.text("Action Required Before Legal Proceedings Are Considered", margin, yPos);
    yPos += 7;

    // ── Outstanding Balance panel ──
    const balancePanelH = 22;
    addRect(margin, yPos, contentWidth, balancePanelH, offWhiteColor, neutralBorderColor);
    const balanceCols = [
      {
        label: "OUTSTANDING BALANCE",
        value: `${pdfCurrency}${(locationState?.amount || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        sub: `Invoice(s): ${locationState?.invoiceNumber || "N/A"}`,
      },
      {
        label: "PAYMENT DUE",
        value: dueDate ? new Date(dueDate).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" }) : "N/A",
      },
      {
        label: "STATUS",
        value: "Final Opportunity",
        sub: "Before Legal Escalation (Pre-LBA)",
      },
    ];
    const balanceColWidth = contentWidth / balanceCols.length;
    balanceCols.forEach((col, i) => {
      const x = margin + i * balanceColWidth + 6;
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(7);
      pdf.setTextColor(...mutedColor);
      pdf.text(col.label, x, yPos + 6.5, { charSpace: 0.3 });
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(i === 0 ? 12.5 : 9.5);
      pdf.setTextColor(i === 0 ? consequenceTextColor[0] : neutralColor[0], i === 0 ? consequenceTextColor[1] : neutralColor[1], i === 0 ? consequenceTextColor[2] : neutralColor[2]);
      const valLines = pdf.splitTextToSize(col.value, balanceColWidth - 10);
      pdf.text(valLines, x, yPos + 12.5);
      if (col.sub) {
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(7);
        pdf.setTextColor(...mutedColor);
        pdf.text(col.sub, x, yPos + 12.5 + valLines.length * 4.2);
      }
    });
    yPos += balancePanelH + 6;

    // ── Checklist columns ──
    const colWidth = (contentWidth - 12) / 2;
    pdf.setFillColor(...primaryColor);
    pdf.rect(margin, yPos, colWidth, 0.8, "F");
    const whyEndY = addChecklistColumn(
      "WHY RESOLVE THIS NOW?",
      ["Avoid legal proceedings", "Prevent additional costs and interest", "Protect your business credit profile", "Resolve the matter quickly and professionally"],
      margin, colWidth, primaryColor, primaryColor,
    );

    const consX = margin + colWidth + 12;
    pdf.setFillColor(...consequenceTextColor);
    pdf.rect(consX, yPos, colWidth, 0.8, "F");
    const consEndY = addChecklistColumn(
      "POTENTIAL CONSEQUENCES OF NON-PAYMENT",
      ["Formal legal proceedings", "Recovery of court fees", "Statutory interest", "Recoverable legal costs", "County Court Judgment (CCJ) (where applicable)", "Enforcement action following judgment"],
      consX, colWidth, consequenceTextColor, consequenceTextColor, "Failure to resolve this matter may result in:",
    );

    const sectionHeight = Math.max(whyEndY, consEndY) - yPos + 4;
    pdf.setDrawColor(...neutralBorderColor);
    pdf.setLineWidth(0.2);
    pdf.line(margin + colWidth + 6, yPos + 4, margin + colWidth + 6, yPos + sectionHeight - 4);
    yPos += sectionHeight + 10;
    checkAndAddPage(40);

    // ── Urgency callout ──
    const urgencyBoxH = 26;
    addRect(margin, yPos, contentWidth, urgencyBoxH, consequenceHeaderColor, consequenceTextColor);
    pdf.setFillColor(...consequenceTextColor);
    pdf.rect(margin, yPos, 2.2, urgencyBoxH, "F");
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(8.5);
    pdf.setTextColor(...consequenceTextColor);
    pdf.text("ACTION REQUIRED", margin + 7, yPos + 6.5, { charSpace: 0.3 });

    const noticeSegments = [
      { text: "Payment or a satisfactory response must be received no later than", bold: false },
      { text: formattedFutureDate, bold: true },
      { text: "Failure to respond within this period may result in the matter being escalated for legal review without further notice.", bold: false },
    ];
    pdf.setFontSize(9.5);
    const noticeLineCount = addWrappedLeft(noticeSegments, margin + 7, yPos + 12, contentWidth - 12, { fontSize: 9.5, lineHeight: 4.4, color: neutralColor });
    const contactY = yPos + 12 + noticeLineCount * 4.4 + 3.5;
    const contactLines = pdf.splitTextToSize("Reach us directly at hello@6ela.io to ensure prompt resolution before any further action.", contentWidth - 12);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(8.5);
    pdf.setTextColor(...consequenceTextColor);
    pdf.text(contactLines, margin + 7, contactY);
    yPos += Math.max(urgencyBoxH, contactY - yPos + 6) + 10;

    checkAndAddPage(30);

    // ── Digital Case Intelligence ──
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9.5);
    pdf.setTextColor(...primaryColor);
    pdf.text("DIGITAL CASE INTELLIGENCE", margin, yPos, { charSpace: 0.2 });
    yPos += 5.5;
    yPos += addText(
      "This matter is being managed through the 6ELA platform. All communications, payment activity, debtor engagement and supporting evidence are automatically recorded to create a complete digital chronology of the matter.",
      margin, yPos, { fontSize: 8.8, color: mutedColor },
    );
    yPos += 6;

    checkAndAddPage(35);

    // ── Evidence Status ──
    const evidenceItems = ["Outstanding balance verified", "Invoice history recorded", "Communication history retained", "Payment history recorded", "Supporting documentation available"];
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9.5);
    pdf.setTextColor(...primaryColor);
    pdf.text("EVIDENCE STATUS", margin, yPos, { charSpace: 0.2 });
    let evY = yPos + 7;
    evidenceItems.forEach((item) => {
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(8.5);
      pdf.setTextColor(...primaryColor);
      pdf.text("\u2713", margin, evY);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8.8);
      pdf.setTextColor(...neutralColor);
      pdf.text(item, margin + 5, evY);
      evY += 5;
    });
    yPos = evY + 5;

    checkAndAddPage(20);

    // ── Branded sign-off ──
    addText("Kind regards,", margin, yPos, { fontSize: 10, isBold: true });
    yPos += 4.5;
    const signoffMarkWidth = drawBrandMark(margin, yPos, { fontSize: 10, isBold: true });
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(10);
    pdf.setTextColor(...neutralColor);
    pdf.text(" Automated Credit Control Platform", margin + signoffMarkWidth, yPos);
    yPos += 5.5;
    pdf.setFont("helvetica", "italic");
    pdf.setFontSize(8.5);
    pdf.setTextColor(...mutedColor);
    pdf.text(`Issued on behalf of ${locationState?.name || "{{CompanyName}}"}`, margin, yPos);
    pdf.setFont("helvetica", "normal");
    yPos += 8;

    // ── Payout Account (Open Banking) ──
    // Matches the "Pay Your Outstanding Balance by EFT" panel in
    // LbaForm.js's live preview — shown for both 'verified' and
    // 'unverified' statuses, same check used there and in
    // SettingsBillingPage, so the reconstructed letter shows the debtor
    // the same payout details they'd have seen at send time.
    if (["verified", "unverified"].includes(openBankingStatus?.status)) {
      const payoutRows = [
        { label: "Bank", value: openBankingStatus.account?.bankName },
        { label: "Account holder", value: openBankingStatus.account?.accountHolderName },
        { label: "Account number", value: openBankingStatus.account?.accountNumberMasked },
        { label: "Branch code", value: openBankingStatus.account?.branchCode },
      ].filter((row) => row.value);

      if (payoutRows.length > 0) {
        checkAndAddPage(35);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(9.5);
        pdf.setTextColor(...primaryColor);
        pdf.text("PAY YOUR OUTSTANDING BALANCE BY EFT", margin, yPos, { charSpace: 0.2 });
        yPos += 5.5;

        const rowHeight = 6.5;
        const numRows = Math.ceil(payoutRows.length / 2);
        const boxH = numRows * rowHeight + 8;
        addRect(margin, yPos, contentWidth, boxH, offWhiteColor, neutralBorderColor);
        const payoutColWidth = contentWidth / 2;
        payoutRows.forEach((row, i) => {
          const col = i % 2;
          const rowIdx = Math.floor(i / 2);
          const x = margin + col * payoutColWidth + 8;
          const y = yPos + 8 + rowIdx * rowHeight;
          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(7.5);
          pdf.setTextColor(...mutedColor);
          pdf.text(row.label, x, y);
          pdf.setFont("helvetica", "bold");
          pdf.setFontSize(8.5);
          pdf.setTextColor(...neutralColor);
          pdf.text(String(row.value), x + payoutColWidth - 16, y, { align: "right" });
        });
        yPos += boxH + 8;
      }
    }

    // ── Footer ──
    checkAndAddPage(30);
    pdf.setDrawColor(...neutralBorderColor);
    pdf.setLineWidth(0.3);
    pdf.line(margin, yPos, margin + contentWidth, yPos);
    yPos += 6;

    const footerColWidth = (contentWidth - 12) / 2;
    let leftY = yPos;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8.5);
    pdf.setTextColor(55, 65, 81);
    const issuedViaPrefix = "Issued via the ";
    pdf.text(issuedViaPrefix, margin, leftY);
    const issuedViaPrefixWidth = pdf.getTextWidth(issuedViaPrefix);
    const issuedViaMarkWidth = drawBrandMark(margin + issuedViaPrefixWidth, leftY, { fontSize: 8.5, isBold: false, color: [55, 65, 81] });
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8.5);
    pdf.setTextColor(55, 65, 81);
    pdf.text(" Accounts Receivable Platform", margin + issuedViaPrefixWidth + issuedViaMarkWidth, leftY);
    leftY += 5.5;

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8.5);
    pdf.setTextColor(55, 65, 81);
    const poweredByPrefix = "Powered by ";
    pdf.text(poweredByPrefix, margin, leftY);
    const poweredByPrefixWidth = pdf.getTextWidth(poweredByPrefix);
    const poweredLogoW = 15, poweredLogoH = 6.7;
    try {
      let loaded = false;
      for (const path of ["assets/logo-horizontal-lba-form.png", "/assets/logo-horizontal-lba-form.png", `${window.location.origin}/assets/logo-horizontal-lba-form.png`]) {
        try { pdf.addImage(path, "PNG", margin + poweredByPrefixWidth, leftY - poweredLogoH + 1.5, poweredLogoW, poweredLogoH, undefined, "FAST"); loaded = true; break; } catch {}
      }
      if (!loaded) throw new Error("Logo failed");
    } catch {
      drawBrandMark(margin + poweredByPrefixWidth, leftY, { fontSize: 8.5, isBold: true });
    }
    leftY += 6.5;

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8.5);
    pdf.setTextColor(...linkColor);
    pdf.textWithLink("www.6ela.com", margin, leftY, { url: "https://www.6ela.com" });
    leftY += 4.5;
    pdf.textWithLink("support@6ela.com", margin, leftY, { url: "mailto:support@6ela.com" });

    const rightX = margin + footerColWidth + 12;
    let rightY = yPos;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9);
    pdf.setTextColor(...primaryColor);
    pdf.text("Litigation Ready\u2122 Case File", rightX, rightY, { charSpace: 0.2 });
    rightY += 5;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7.5);
    pdf.setTextColor(...mutedColor);
    const escLines = pdf.splitTextToSize(
      "Every action is securely logged into a chronological, auditable case file. Where instructed, this can be shared with legal professionals to support pre-action review.",
      footerColWidth,
    );
    pdf.text(escLines, rightX, rightY);

    yPos += Math.max(leftY - yPos, escLines.length * 3.4 + 5) + 8;

    // ── Important Legal Notice ──
    checkAndAddPage(45);
    pdf.setDrawColor(...neutralBorderColor);
    pdf.setLineWidth(0.2);
    pdf.line(margin, yPos, margin + contentWidth, yPos);
    yPos += 6;

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9.5);
    pdf.setTextColor(...neutralColor);
    pdf.text("Important Legal Notice", margin, yPos, { charSpace: 0.2 });
    yPos += 5.5;

    const legalNoticeParagraphs = [
      "This letter forms part of the creditor's formal pre-action recovery process. Failure to respond may result in the matter being referred to appointed legal professionals for review. Any subsequent legal proceedings may rely upon the documented evidence maintained within the 6ELA platform, including communications, invoices, payment history and supporting documentation.",
      "Where proceedings are commenced, the creditor may seek recovery of the outstanding balance together with any applicable statutory interest, court fees and recoverable legal costs.",
      "This communication has been issued via the 6ELA Accounts Receivable Platform on behalf of the creditor and should be treated as an important legal communication requiring immediate attention.",
    ];
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7.3);
    pdf.setTextColor(...mutedColor);
    legalNoticeParagraphs.forEach((para) => {
      checkAndAddPage(16);
      const lines = pdf.splitTextToSize(para, contentWidth);
      pdf.text(lines, margin, yPos, { lineHeightFactor: 1.3 });
      yPos += lines.length * 3.7 + 3.5;
    });

    return pdf;
  }, [locationState, lbaId, dueDate, openBankingStatus, preLbaSentDate, currencySymbol, currencyCode]);

  const buildPreLbaFilename = useCallback(() => {
    const safeName = locationState?.name ? locationState.name.replace(/\s+/g, "_") : "document";
    return `Pre-LBA_${safeName}_${lbaId || "reference"}.pdf`;
  }, [locationState, lbaId]);

  const handleViewSentPreLba = useCallback(() => {
    try {
      setIsBuildingPreLbaPreview(true);
      const pdf = buildPreLbaPdf();
      const blob = pdf.output("blob");
      const url = URL.createObjectURL(blob);
      preLbaObjectUrlsRef.current.push(url);
      setPreLbaPreviewUrl(url);
    } catch (e) {
      console.error("Failed to rebuild Pre-LBA preview:", e);
      alert("Couldn't load the sent Pre-LBA letter right now. Please try again.");
    } finally {
      setIsBuildingPreLbaPreview(false);
    }
  }, [buildPreLbaPdf]);

  const closePreLbaPreview = useCallback(() => setPreLbaPreviewUrl(null), []);

  const handleDownloadSentPreLba = useCallback(() => {
    try {
      const pdf = buildPreLbaPdf();
      pdf.save(buildPreLbaFilename());
    } catch (e) {
      alert("Couldn't download the Pre-LBA letter right now. Please try again.");
    }
  }, [buildPreLbaPdf, buildPreLbaFilename]);

  /* ── Sync dueDate ── */
  useEffect(() => {
    if (locationState?.dueDate) {
      setDueDate(locationState.dueDate);
      setOverdueDays(calculateOverdueDays(locationState.dueDate));
    }
  }, [locationState?.dueDate]);

  /* ── Fetch communication history ── */
  useEffect(() => {
    if (!lbaId) return;
    const fetch_ = async () => {
      try {
        setLoadingHistory(true);
        const result = await debtorDetailsService.getCommunicationHistory(lbaId);
        if (result.success) setCommunicationHistory(result.history);
      } catch (e) {
        console.error("Comm history error:", e);
      } finally {
        setLoadingHistory(false);
      }
    };
    fetch_();
  }, [lbaId]);

  /* ── Fetch status ── */
  useEffect(() => {
    if (!lbaId || !userId) return;
    debtorDetailsService.getLbaStatus(lbaId, userId).then((r) => {
      if (r.success) setStatuses((p) => ({ ...p, [lbaId]: r.status === "Resolved" ? "Settled" : r.status }));
    }).catch(console.error);
  }, [lbaId, userId]);

  /* ── Fetch documents — FIXED ── */
  useEffect(() => {
    if (!lbaId) return;

    const fetchDocs = async () => {
      try {
        setLoading(true);

        const res = await fetch(
          `${API_URL}/api/official_documents/documents/${lbaId}`,
          { method:"GET", headers:{ "Content-Type":"application/json", Authorization:`Bearer ${token}` } }
        );

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();
        console.log("📄 Documents API response:", data); // debug — remove when confirmed working

        // Handle both { documents: [...] } and a flat array
        const allDocs = Array.isArray(data.documents)
          ? data.documents
          : Array.isArray(data)
            ? data
            : [];

        // Flatten every doc → every file, preserving the per-file index
        const flatFiles = allDocs.flatMap((doc) =>
          (doc.files || []).map((f, fileIndex) => ({
            // spread all file fields (fileName, fileSize, etc.)
            ...f,
            // normalise name + size fields regardless of API key naming
            fileName:   f.fileName   || f.name        || `Document ${fileIndex + 1}`,
            fileSize:   f.fileSize   || f.size         || 0,
            // meta needed for preview / download
            lbaId:      doc.lbaId    || lbaId,
            documentId: doc.documentId || doc._id || doc.id,
            mimeType:   f.mimeType   || doc.mimeType  || "",
            index:      fileIndex,   // ← explicit index for the preview URL
          }))
        );

        console.log("📄 Flat files:", flatFiles); // debug — remove when confirmed working

        setDocuments(flatFiles);
        setInvoiceCount(flatFiles.length);
      } catch (e) {
        console.error("Error fetching documents:", e);
        setDocuments([]);
        setInvoiceCount(0);
      } finally {
        setLoading(false);
      }
    };

    fetchDocs();
  }, [lbaId, token]);

  /* ── Handlers ── */
  const handlePreview = (docLbaId, fileIndex) => {
    const url = `${API_URL}/api/official_documents/file/${docLbaId}/${fileIndex}`;
    window.open(url, "_blank");
  };

  const handleDownloadAll = async () => {
    if (!documents.length) { alert("No documents available for download."); return; }
    try {
      const zip    = new JSZip();
      const folder = zip.folder("Documents");
      const used   = new Set();

      const uniqueName = (raw, idx) => {
        const dot  = raw.lastIndexOf(".");
        const base = dot !== -1 ? raw.slice(0, dot) : raw;
        const ext  = dot !== -1 ? raw.slice(dot)    : "";
        let name   = raw;
        let n = 2;
        while (used.has(name)) { name = `${base}_${idx}_${n}${ext}`; n++; }
        used.add(name);
        return name;
      };

      for (const f of documents) {
        try {
          const url = `${API_URL}/api/official_documents/file/${f.lbaId}/${f.index}`;
          const r   = await fetch(url, { headers:{ Authorization:`Bearer ${token}` } });
          if (r.ok) folder.file(uniqueName(f.fileName, f.index), await r.blob());
          else console.warn(`Skipped file ${f.index} — status ${r.status}`);
        } catch (e) {
          console.error(`Error downloading file ${f.index}:`, e);
        }
      }

      if (!Object.keys(folder.files).length) { alert("No files could be downloaded."); return; }
      saveAs(await zip.generateAsync({ type:"blob" }), `Documents-${new Date().toISOString().split("T")[0]}.zip`);
    } catch (e) {
      alert("Failed to download files. Please try again.");
    }
  };

  const handleMarkAsSettled = async () => {
    try {
      setIsSettling(true);
      const result = await debtorDetailsService.markAsSettled(lbaId, userId);
      if (result.success) {
        setStatuses((p) => ({ ...p, [lbaId]: result.status || "Settled" }));
        alert(result.message || "Status updated successfully!");
      }
    } catch (e) {
      alert(`Error: ${e.message}`);
    } finally {
      setIsSettling(false);
    }
  };

  const handleDateTimeSelect = (r) => setSelectedDateTimeRange(r);

  const currentStatus = statuses[lbaId] || "Unsettled";
  const isSettled     = currentStatus === "Settled";

  const filteredHistory = communicationHistory.filter((entry) => {
    if (!searchTerm.trim()) return true;
    const s = searchTerm.toLowerCase();
    return (
      entry.description?.toLowerCase().includes(s) ||
      entry.userName?.toLowerCase().includes(s) ||
      entry.userEmail?.toLowerCase().includes(s) ||
      new Date(entry.createdAt)
        .toLocaleString("en-GB", { day:"2-digit", month:"2-digit", year:"2-digit", hour:"2-digit", minute:"2-digit", hour12:true })
        .toLowerCase().includes(s)
    );
  });

  /* ── Metric cards ── */
  const metricCards = [
    {
      label: "Total Debt",
      value: `${currencySymbol}${(locationState?.amount || 0).toLocaleString("en-US", { minimumFractionDigits:2, maximumFractionDigits:2 })}`,
      accentBar: "#7C3AED", iconBg: "#F5F3FF",
      icon: <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M9.5 13.75c0 .97.75 1.75 1.67 1.75h1.88c.8 0 1.45-.68 1.45-1.53 0-.91-.4-1.24-.99-1.45l-3.01-1.05c-.59-.21-.99-.53-.99-1.45 0-.84.65-1.53 1.45-1.53h1.88c.92 0 1.67.78 1.67 1.75M12 7.5v9" stroke="#7C3AED" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M22 12c0 5.52-4.48 10-10 10S2 17.52 2 12 6.48 2 12 2" stroke="#7C3AED" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>,
    },
    {
      label: "Overdue",
      value: overdueDays > 0 ? `${overdueDays} days` : "Not overdue",
      accentBar: "#F59E0B", iconBg: "#FFFBEB",
      icon: <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M12 9v5M12 21.41H5.94c-3.47 0-4.92-2.48-3.24-5.51l3.12-5.62L8.76 5c1.78-3.21 4.7-3.21 6.48 0l2.94 5.29 3.12 5.62c1.68 3.03.22 5.51-3.24 5.51H12v-.01Z" stroke="#D97706" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M11.995 17h.009" stroke="#D97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>,
    },
    {
      label: "Payment Due By",
      value: loadingHistory ? "…" : paymentDeadlineLabel,
      accentBar: "#2563EB", iconBg: "#EFF6FF",
      icon: <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M8 2v3M16 2v3M3.5 9.09h17M21 8.5V17c0 3-1.5 5-5 5H8c-3.5 0-5-2-5-5V8.5c0-3 1.5-5 5-5h8c3.5 0 5 2 5 5Z" stroke="#2563EB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M15.7 13.7h.01M15.7 16.7h.01M11.99 13.7h.01M11.99 16.7h.01M8.29 13.7h.01M8.29 16.7h.01" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>,
    },
    {
      label: "Risk Level",
      value: "Low",
      accentBar: "#059669", iconBg: "#ECFDF5",
      icon: <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M12 22c5.5 0 10-4.5 10-10S17.5 2 12 2 2 6.5 2 12s4.5 10 10 10Z" stroke="#059669" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="m7.75 12 2.83 2.83 5.67-5.66" stroke="#059669" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>,
      badge: { label:"AI Insights", color:"#059669", bg:"#ECFDF5" },
    },
  ];

  return (
    <>
      <style>{dashStyles}</style>

      <div className="lba2-root w-full overflow-visible">
        <NavigationPanel />

        <div style={{ position:"absolute", top:136, width:"100%", padding:"0 96px 64px", display:"flex", flexDirection:"column", gap:28 }}>

          {/* Breadcrumb */}
          <div className="lba2-breadcrumb">
            <a href="/lba">Letter before action (LBA)</a>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="m9 18 6-6-6-6" stroke="#CBD5E1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            <span className="active">{locationState?.name || "Debtor details"}</span>
          </div>

          {/* Metric cards */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:18 }}>
            {metricCards.map((card) => (
              <div key={card.label} className="lba2-card lba2-metric-card" style={{ padding:24 }}>
                <div className="accent-bar" style={{ background:card.accentBar }} />
                <div style={{ marginTop:8 }}>
                  <div style={{ width:40, height:40, borderRadius:10, background:card.iconBg, display:"flex", alignItems:"center", justifyContent:"center", marginBottom:18, border:"1px solid #E2E8F0" }}>{card.icon}</div>
                  <div style={{ fontSize:11, fontWeight:700, color:"#94A3B8", textTransform:"uppercase", letterSpacing:".06em", marginBottom:6 }}>{card.label}</div>
                  <div className="lba2-mono" style={{ fontSize:26, fontWeight:500, color:"#0F172A", lineHeight:1.1 }}>{card.value}</div>
                  {card.badge && (
                    <div style={{ marginTop:10, display:"inline-flex", alignItems:"center", gap:4, background:card.badge.bg, color:card.badge.color, padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:700 }}>
                      ✦ {card.badge.label}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Debtor info + Quick actions */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:18 }}>

            {/* Debtor info */}
            <div className="lba2-card" style={{ overflow:"hidden" }}>
              <div style={{ padding:"20px 24px", borderBottom:"1px solid #E2E8F0", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <div>
                  <div className="lba2-section-title">{locationState?.name || "Debtor"}</div>
                  <div className="lba2-section-sub">Debtor information</div>
                </div>
                <span className="lba2-badge" style={{ background:isSettled?"#F0FDF4":"#FEF2F2", color:isSettled?"#16A34A":"#DC2626" }}>
                  <span style={{ width:6, height:6, borderRadius:"50%", background:isSettled?"#16A34A":"#DC2626", display:"inline-block" }} />
                  {currentStatus}
                </span>
              </div>
              <div style={{ padding:"20px 24px", display:"flex", flexDirection:"column", gap:14 }}>
                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                  <div style={{ width:32, height:32, borderRadius:8, background:"#EFF6FF", display:"flex", alignItems:"center", justifyContent:"center" }}>
                    <Location size={16} color="#2563EB" />
                  </div>
                  <div>
                    <div style={{ fontSize:11, color:"#94A3B8", fontWeight:600, textTransform:"uppercase", letterSpacing:".05em" }}>City</div>
                    <div style={{ fontSize:14, fontWeight:600, color:"#0F172A" }}>{locationState?.address?.city || "—"}</div>
                  </div>
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                  <div style={{ width:32, height:32, borderRadius:8, background:"#EFF6FF", display:"flex", alignItems:"center", justifyContent:"center" }}>
                    <Sms size={16} color="#2563EB" />
                  </div>
                  <div>
                    <div style={{ fontSize:11, color:"#94A3B8", fontWeight:600, textTransform:"uppercase", letterSpacing:".05em" }}>Email</div>
                    <div style={{ fontSize:14, fontWeight:600, color:"#0F172A" }}>{locationState?.email || "—"}</div>
                  </div>
                </div>
              </div>
              <div style={{ padding:"14px 24px", borderTop:"1px solid #E2E8F0", background:"#FAFBFD", display:"flex", justifyContent:"flex-end" }}>
                <button className="lba2-action-btn" onClick={handleMarkAsSettled} disabled={isSettling || isSettled}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M12 22c5.5 0 10-4.5 10-10S17.5 2 12 2 2 6.5 2 12s4.5 10 10 10Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="m7.75 12 2.83 2.83 5.67-5.66" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  {isSettling ? "Processing…" : "Mark as settled"}
                </button>
              </div>
            </div>

            {/* Quick actions */}
            <div className="lba2-card" style={{ overflow:"hidden" }}>
              <div style={{ padding:"20px 24px", borderBottom:"1px solid #E2E8F0" }}>
                <div className="lba2-section-title">Quick Actions</div>
                <div className="lba2-section-sub">Manage this debtor</div>
              </div>
              <div style={{ padding:"20px 24px", display:"flex", flexDirection:"column", gap:12 }}>
                <div
                  onClick={openLawFirm}
                  style={{ display:"flex", alignItems:"center", gap:14, padding:"14px 16px", borderRadius:12, border:"1px solid #E2E8F0", background:"#fff", cursor:"pointer", transition:"all .2s" }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor="#2563EB"; e.currentTarget.style.boxShadow="0 4px 16px rgba(37,99,235,.1)"; e.currentTarget.style.transform="translateX(4px)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor="#E2E8F0"; e.currentTarget.style.boxShadow="none"; e.currentTarget.style.transform="none"; }}
                >
                  <div style={{ width:40, height:40, borderRadius:10, background:"#EFF6FF", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="m20.01 18.51-4.95-4.95M15.06 13.56l-3.54 3.54c-.78.78-2.05.78-2.83 0l-4.24-4.24c-.78-.78-.78-2.05 0-2.83l7.07-7.07c.78-.78 2.05-.78 2.83 0l4.24 4.24c.78.78.78 2.05 0 2.83l-3.53 3.53ZM2 21h6M6.56 7.92l7.07 7.07" stroke="#2563EB" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:14, fontWeight:700, color:"#0F172A" }}>Contact Law Firm</div>
                    <div style={{ fontSize:12, color:"#64748B", marginTop:2 }}>Escalate this case to legal representation.</div>
                  </div>
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="m9 18 6-6-6-6" stroke="#CBD5E1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </div>

                <div
                  onClick={handleViewSentPreLba}
                  style={{ display:"flex", alignItems:"center", gap:14, padding:"14px 16px", borderRadius:12, border:"1px solid #E2E8F0", background:"#fff", cursor: isBuildingPreLbaPreview ? "wait" : "pointer", transition:"all .2s", opacity: isBuildingPreLbaPreview ? 0.6 : 1 }}
                  onMouseEnter={(e) => { if (isBuildingPreLbaPreview) return; e.currentTarget.style.borderColor="#2563EB"; e.currentTarget.style.boxShadow="0 4px 16px rgba(37,99,235,.1)"; e.currentTarget.style.transform="translateX(4px)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor="#E2E8F0"; e.currentTarget.style.boxShadow="none"; e.currentTarget.style.transform="none"; }}
                >
                  <div style={{ width:40, height:40, borderRadius:10, background:"#F5F3FF", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                    {isBuildingPreLbaPreview ? (
                      <span className="lba2-spinner" style={{ width:16, height:16, borderColor:"#DDD6FE", borderTopColor:"#7C3AED" }} />
                    ) : (
                      <EyeIcon size={18} color="#7C3AED" />
                    )}
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:14, fontWeight:700, color:"#0F172A" }}>View sent Pre-LBA</div>
                    <div style={{ fontSize:12, color:"#64748B", marginTop:2 }}>Review the exact letter that was sent to this debtor.</div>
                  </div>
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="m9 18 6-6-6-6" stroke="#CBD5E1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </div>
              </div>
            </div>
          </div>

          {/* Communication history */}
          <div className="lba2-card" style={{ overflow:"hidden" }}>
            <div style={{ padding:"18px 24px", borderBottom:"1px solid #E2E8F0", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <span className="lba2-section-title">Communication history</span>
                <span className="lba2-count-pill">
                  <span style={{ width:6, height:6, borderRadius:"50%", background:"#2563EB", display:"inline-block" }} />
                  {communicationHistory.length} {communicationHistory.length === 1 ? "entry" : "entries"}
                </span>
              </div>
              <button className="lba2-dl-btn" onClick={() => setDownloadModalTablesOpen(true)}>
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M9 11v6l2-2M9 17l-2-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M22 10v5c0 5-2 7-7 7H9c-5 0-7-2-7-7V9c0-5 2-7 7-7h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M22 10h-4c-3 0-4-1-4-4V2l8 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                Download
              </button>
            </div>
            <div style={{ padding:"12px 24px", borderBottom:"1px solid #F1F5F9", background:"#FAFBFD", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <div className="lba2-search">
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M11.5 21a9.5 9.5 0 1 0 0-19 9.5 9.5 0 0 0 0 19ZM22 22l-2-2" stroke="#94A3B8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                <input type="text" placeholder="Search history…" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
              </div>
              <button className="lba2-filter-btn" onClick={() => setDateFilterOpen(true)}>
                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                Filter
              </button>
            </div>
            <div style={{ overflowX:"auto" }}>
              <table className="lba2-table">
                <thead>
                  <tr>{["Date & Time","Description","Sender"].map((h) => <th key={h}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {loadingHistory ? (
                    <tr><td colSpan="3" style={{ textAlign:"center", color:"#94A3B8", padding:40 }}>
                      <span className="lba2-spinner" style={{ marginRight:8 }} />Loading…
                    </td></tr>
                  ) : filteredHistory.length === 0 ? (
                    <tr><td colSpan="3" style={{ textAlign:"center", color:"#94A3B8", padding:40 }}>{searchTerm ? "No results found" : "No communication history"}</td></tr>
                  ) : filteredHistory.map((entry, i) => (
                    <tr key={entry._id || i}>
                      <td style={{ whiteSpace:"nowrap", color:"#64748B", fontSize:13 }}>
                        {new Date(entry.createdAt).toLocaleString("en-GB", { day:"2-digit", month:"2-digit", year:"2-digit", hour:"2-digit", minute:"2-digit", hour12:true })}
                      </td>
                      <td style={{ maxWidth:320 }}>{entry.description}</td>
                      <td style={{ fontSize:13, color:"#64748B" }}>
                        <div style={{ fontWeight:600, color:"#0F172A" }}>{entry.userName}</div>
                        <div style={{ fontSize:12, color:"#94A3B8" }}>{entry.userEmail}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Attached documents — FIXED ── */}
          <div className="lba2-card" style={{ overflow:"hidden" }}>
            <div style={{ padding:"18px 24px", borderBottom:"1px solid #E2E8F0", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <div>
                <div className="lba2-section-title">Attached documents</div>
                <div className="lba2-section-sub">
                  {loading ? "Loading…" : `${documents.length} document${documents.length !== 1 ? "s" : ""} linked to this debtor`}
                </div>
              </div>
              <button className="lba2-dl-btn" onClick={handleDownloadAll} disabled={loading || !documents.length}>
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M9 11v6l2-2M9 17l-2-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M22 10v5c0 5-2 7-7 7H9c-5 0-7-2-7-7V9c0-5 2-7 7-7h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M22 10h-4c-3 0-4-1-4-4V2l8 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                Download all
              </button>
            </div>

            <div style={{ padding:24 }}>
              {loading ? (
                <div className="lba2-empty-docs">
                  <span className="lba2-spinner" />
                  <span style={{ color:"#64748B", fontSize:14 }}>Loading documents…</span>
                </div>
              ) : documents.length === 0 ? (
                <div className="lba2-empty-docs">
                  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none">
                    <path d="M22 10v5c0 5-2 7-7 7H9c-5 0-7-2-7-7V9c0-5 2-7 7-7h5" stroke="#CBD5E1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M22 10h-4c-3 0-4-1-4-4V2l8 8Z" stroke="#CBD5E1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <div style={{ fontSize:15, fontWeight:600, color:"#64748B" }}>No documents found</div>
                  <div style={{ fontSize:13, color:"#94A3B8", maxWidth:340 }}>
                    No files have been uploaded for this Pre-LBA yet. Documents are attached when the Pre-LBA form is submitted.
                  </div>
                </div>
              ) : (
                <div style={{ display:"flex", flexWrap:"wrap", gap:18 }}>
                  {documents.map((f, i) => (
                    <FlatFileCard
                      key={`${f.documentId}-${f.index ?? i}`}
                      file={f}
                      onPreview={() => handlePreview(f.lbaId, f.index ?? i)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          <Footer />
        </div>
      </div>

      {/* Modals */}
      {filePreview && (
        <FilePreviewModal fileUrl={filePreview.url} fileType={filePreview.type} onClose={() => setFilePreview(null)} />
      )}
      {preLbaPreviewUrl && (
        <div
          style={{ position:"fixed", inset:0, zIndex:60, display:"flex", alignItems:"center", justifyContent:"center", background:"rgba(0,0,0,0.55)" }}
          onClick={(e) => { if (e.target === e.currentTarget) closePreLbaPreview(); }}
        >
          <div style={{ background:"#fff", borderRadius:16, width:"90%", maxWidth:780, maxHeight:"90vh", display:"flex", flexDirection:"column", overflow:"hidden", boxShadow:"0 20px 60px rgba(0,0,0,0.3)" }}>
            <div style={{ padding:"14px 20px", borderBottom:"1px solid #E2E8F0", display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0 }}>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <FileTextIcon size={16} style={{ color:"#2563EB" }} />
                <span style={{ fontFamily:"'DM Sans', sans-serif", fontSize:14, fontWeight:700, color:"#0F172A" }}>
                  Sent Pre-LBA letter
                </span>
              </div>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <button
                  onClick={handleDownloadSentPreLba}
                  type="button"
                  style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"6px 12px", borderRadius:8, background:"#EFF6FF", border:"1px solid #BFDBFE", color:"#2563EB", fontFamily:"'DM Sans', sans-serif", fontSize:12, fontWeight:700, cursor:"pointer" }}
                >
                  <DownloadIcon size={13} />
                  Download
                </button>
                <button
                  onClick={closePreLbaPreview}
                  type="button"
                  style={{ width:28, height:28, borderRadius:8, background:"#F1F5F9", border:"none", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}
                >
                  <CloseIcon size={14} style={{ color:"#64748B" }} />
                </button>
              </div>
            </div>
            <iframe
              src={preLbaPreviewUrl}
              title="Sent Pre-LBA letter"
              style={{ border:"none", flex:1, width:"100%", minHeight:480 }}
            />
          </div>
        </div>
      )}
      {isDateFilterOpen && (
        <PortalPopup overlayColor="rgba(113,113,113,0.3)" placement="Centered" onOutsideClick={() => setDateFilterOpen(false)}>
          <DateFilter onClose={() => setDateFilterOpen(false)} onDateTimeSelect={handleDateTimeSelect} />
        </PortalPopup>
      )}
      {isDebtorDeletionOpen && (
        <PortalPopup overlayColor="rgba(113,113,113,0.3)" placement="Centered" onOutsideClick={() => setDebtorDeletionOpen(false)}>
          <DebtorDeletion onClose={() => setDebtorDeletionOpen(false)} />
        </PortalPopup>
      )}
      {isImagePreviewOpen && (
        <PortalPopup overlayColor="rgba(113,113,113,0.3)" placement="Centered" onOutsideClick={() => setImagePreviewOpen(false)}>
          <ImagePreview onClose={() => setImagePreviewOpen(false)} />
        </PortalPopup>
      )}
      {isLBAReminderOpen && (
        <PortalPopup overlayColor="rgba(113,113,113,0.3)" placement="Centered" onOutsideClick={() => setLBAReminderOpen(false)}>
          <LBAReminder onClose={() => setLBAReminderOpen(false)} />
        </PortalPopup>
      )}
      {isDownloadModalTablesOpen && (
        <PortalPopup overlayColor="rgba(113,113,113,0.3)" placement="Centered" onOutsideClick={() => setDownloadModalTablesOpen(false)}>
          <DownloadModalTables onClose={() => setDownloadModalTablesOpen(false)} tableData={communicationHistory} tag="comm" />
        </PortalPopup>
      )}
      {isLawFirmOpen && (
        <PortalPopup overlayColor="rgba(113,113,113,0.3)" placement="Centered" onOutsideClick={closeLawFirm}>
          <LawFirm
            onClose={closeLawFirm}
            debtorId={lbaId}
            formType="lba"
            debtorAddress={locationState?.address}
            debtorName={locationState?.name}
            debtorAmount={locationState?.amount}
          />
        </PortalPopup>
      )}
      {isNavigationDropdownOpen && (
        <PortalPopup overlayColor="rgba(113,113,113,0.3)" placement="Centered" onOutsideClick={() => setNavigationDropdownOpen(false)}>
          <NavigationDropdown onClose={() => setNavigationDropdownOpen(false)} />
        </PortalPopup>
      )}
      {isLogoutConfirmationOpen && (
        <PortalPopup overlayColor="rgba(0,0,0,0.25)" placement="Centered" onOutsideClick={() => setLogoutConfirmationOpen(false)}>
          <LogoutConfirmation onClose={() => setLogoutConfirmationOpen(false)} />
        </PortalPopup>
      )}
    </>
  );
};

export default LBA_2;